import { useState } from "react";

import FilterAltRoundedIcon from "@mui/icons-material/FilterAltRounded";
import { Box, IconButton, Menu, MenuItem, Typography } from "@mui/material";

import { useTranslation } from "@/translations";

export interface ColumnFilterOption {
  value: string;
  label: string;
}

interface Props {
  label: string;
  options: ColumnFilterOption[];
  selectedValue: string | null;
  onChange: (value: string | null) => void;
}

/** Data-grid column header with an inline single-select filter menu. Shared across table views. */
export function ColumnFilterHeader({ label, options, selectedValue, onChange }: Props) {
  const { t } = useTranslation();
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isActive = selectedValue !== null;

  const handleSelect = (value: string | null) => {
    onChange(value);
    setAnchorEl(null);
  };

  return (
    <Box display="flex" alignItems="center" gap={0.5}>
      <Typography variant="body2" fontWeight={700} noWrap>
        {label}
      </Typography>
      <IconButton
        size="small"
        aria-label={t("common.columnFilter.filterBy", { column: label })}
        onClick={(event) => {
          event.stopPropagation();
          setAnchorEl(event.currentTarget);
        }}
        color={isActive ? "primary" : "default"}
        sx={{ flexShrink: 0 }}
      >
        <FilterAltRoundedIcon fontSize="small" />
      </IconButton>
      <Menu anchorEl={anchorEl} open={anchorEl !== null} onClose={() => setAnchorEl(null)}>
        <MenuItem selected={selectedValue === null} onClick={() => handleSelect(null)}>
          {t("common.all")}
        </MenuItem>
        {options.map((option) => (
          <MenuItem
            key={option.value}
            selected={selectedValue === option.value}
            onClick={() => handleSelect(option.value)}
          >
            {option.label}
          </MenuItem>
        ))}
      </Menu>
    </Box>
  );
}
